/**
 * Where Cancel leaves the wizard. It returns the person to wherever they came
 * from when that was the people list (keeping its search) or a person's
 * profile (any tab); otherwise it opens the primary's profile, or the list when
 * no primary is selected.
 */
export type CancelTarget =
  | { kind: 'url'; url: string }
  | { kind: 'person'; personId: string }
  | { kind: 'people-list' };

const PEOPLE_LIST = /^\/workspaces\/\d+\/(?:journeys\/)?(?:people|profiles)\/?$/i;
const PERSON_PROFILE =
  /^\/workspaces\/\d+\/journeys\/(?:people|profiles)\/(?!merge|new|create|welcome)[^/?#]+(?:\/|$)/i;

function pathnameOf(url: string): string {
  return url.split(/[?#]/, 1)[0] ?? url;
}

/**
 * @param previousUrl the app-relative URL (path plus query) the person arrived from, if any
 * @param primaryCustomerId the selected primary's id, if any
 */
export function resolveCancelTarget(previousUrl: string | null, primaryCustomerId: string | null): CancelTarget {
  if (previousUrl != null) {
    const pathname = pathnameOf(previousUrl);
    if (PEOPLE_LIST.test(pathname)) {
      return { kind: 'url', url: previousUrl };
    }
    if (PERSON_PROFILE.test(pathname)) {
      return { kind: 'url', url: pathname };
    }
  }
  if (primaryCustomerId != null) {
    return { kind: 'person', personId: primaryCustomerId };
  }
  return { kind: 'people-list' };
}

/**
 * The URL the wizard was entered from: the latest history entry that is not
 * the wizard itself (selecting people rewrites its own query string, which the
 * history records as further entries).
 */
export function previousUrlFrom(history: ReadonlyArray<{ url: string }>, currentPathname: string): string | null {
  for (let index = history.length - 1; index >= 0; index -= 1) {
    const entry = history[index];
    if (entry != null && pathnameOf(entry.url) !== currentPathname) {
      return entry.url;
    }
  }
  return null;
}
